import ExcelJS from 'exceljs';
import { mergedSheetCellValue, type MergedSheetCell } from '@eureca/xlsx-export';

/**
 * Gera um `.xlsx` de uma matriz de strings e dispara o download no browser.
 * Compartilhado pelas duas exportações de base (por etapa e por programa) —
 * a geração do arquivo é a mesma; só a matriz muda.
 *
 * O conteúdo já deve vir higienizado (`sanitizeXmlText`): o ExcelJS escreve o
 * texto cru no `sharedStrings.xml` e um único code point proibido em XML
 * produz um arquivo que o Excel recusa abrir.
 */
export async function downloadSheetAsXlsx(
  matrix: { header: string[]; rows: string[][] },
  options: { fileName: string; sheetName?: string; columnWidth?: number }
): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(options.sheetName ?? 'Respostas');

  if (matrix.header.length > 0) {
    sheet.addRow(matrix.header);
    sheet.getRow(1).font = { bold: true };
  }
  for (const row of matrix.rows) sheet.addRow(row);
  sheet.columns.forEach((col) => {
    col.width = options.columnWidth ?? 28;
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  triggerDownload(blob, options.fileName);
}

/**
 * Gera um `.xlsx` com CABEÇALHO DE DOIS NÍVEIS (linha 1 = grupos mesclados,
 * linha 2 = colunas, dados a partir da linha 3 — EU-654 RN-14) e suporte a
 * células com hyperlink rotulado (RN-17/18). O `downloadSheetAsXlsx` acima
 * segue intacto — as exportações de um cabeçalho (guards V55/V56) não mudam.
 */
export async function downloadMergedSheetAsXlsx(
  matrix: {
    groups: { title: string; span: number }[];
    header: string[];
    rows: MergedSheetCell[][];
  },
  options: { fileName: string; sheetName?: string; columnWidth?: number }
): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(options.sheetName ?? 'Respostas');

  // Linha 1 — o título ocupa a primeira coluna do grupo; o merge espalha.
  const groupRow: string[] = [];
  for (const group of matrix.groups) {
    groupRow.push(group.title, ...Array<string>(Math.max(group.span - 1, 0)).fill(''));
  }
  sheet.addRow(groupRow);
  let col = 1;
  for (const group of matrix.groups) {
    if (group.span > 1) sheet.mergeCells(1, col, 1, col + group.span - 1);
    col += group.span;
  }
  sheet.getRow(1).font = { bold: true };
  sheet.getRow(1).alignment = { horizontal: 'center' };

  sheet.addRow(matrix.header);
  sheet.getRow(2).font = { bold: true };

  for (const row of matrix.rows) {
    const added = sheet.addRow(row.map((cell) => (typeof cell === 'string' ? cell : '')));
    row.forEach((cell, index) => {
      if (typeof cell === 'string') return;
      // Mesma tradução do writer streaming do Core (EU-765): hyperlink
      // rotulado, percentual numérico ou dia civil.
      const { value, numFmt, hyperlink } = mergedSheetCellValue(cell);
      const target = added.getCell(index + 1);
      target.value = value;
      if (numFmt) target.numFmt = numFmt;
      if (hyperlink) target.font = { color: { argb: 'FF0563C1' }, underline: true };
    });
  }

  sheet.columns.forEach((column) => {
    column.width = options.columnWidth ?? 28;
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  triggerDownload(blob, options.fileName);
}

export function triggerDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
